/**
 * Turns the deployed app's subscription tiers (`AppConfig.signup.tiers`) into
 * the strings the signup and billing pages render.
 *
 * Both pages call `buildTierViews`, so the app's packaging can only ever be
 * described in one place. The tier configs carry ids, prices and currencies;
 * all human-readable copy comes from `subscription.tiers.<i18nKey>.*` in the
 * translation catalogs.
 */
import type { AppTierConfig, CurrencyCode, TierPrices } from '../config/apps';

/** Signature shared by react-admin's `useTranslate()` and `usePreAuthI18n()`. */
export type TranslateFn = (key: string, options?: Record<string, unknown>) => string;

/**
 * The currency an organization in a given country is billed in.
 *
 * A table rather than `country === 'no'` so the Nordics can be priced without
 * touching a single call site. Countries not listed take {@link DEFAULT_CURRENCY}.
 */
const CURRENCY_BY_COUNTRY: Readonly<Record<string, CurrencyCode>> = { no: 'NOK' };

/** What an organization is billed in when its country names no currency of its own. */
export const DEFAULT_CURRENCY: CurrencyCode = 'EUR';

/**
 * The currency to quote for an organization in `country`.
 *
 * Keyed on the **organization's country**, never on the reader's language: a
 * Norwegian speaker registering a company abroad is quoted that company's currency,
 * and an English-reading accountant at a Norwegian company is quoted kroner. Locale
 * still decides how the amount is written — see {@link formatTierPrice}.
 */
export const currencyForCountry = (country?: string | null): CurrencyCode =>
    CURRENCY_BY_COUNTRY[(country ?? '').trim().toLowerCase()] ?? DEFAULT_CURRENCY;

/**
 * A tier's price in `currency`, falling back to one it does declare.
 *
 * The fallback cannot fire for the apps shipped today (sirktek and vardly are
 * Norway-only, ledger17 prices both currencies), but a blank or `0,-` price on a
 * card is a worse failure than a price in an unexpected currency. The resolved
 * currency comes back with the amount so the caller formats what it actually has
 * rather than what it asked for.
 */
export const priceIn = (
    prices: TierPrices,
    currency: CurrencyCode,
): { amount: number; currency: CurrencyCode } => {
    const requested = prices[currency];
    if (requested !== undefined) return { amount: requested, currency };

    const [fallbackCurrency, fallbackAmount] = Object.entries(prices)[0] as [CurrencyCode, number];
    return { amount: fallbackAmount, currency: fallbackCurrency };
};

/**
 * BCP-47 locale for `Intl`, given one of our i18n locale codes. Our Norwegian
 * catalog is registered as `no` (and `nb`), neither of which carries a region,
 * so map both onto `nb-NO` for number/currency formatting.
 */
export const intlLocaleOf = (locale: string): string =>
    locale === 'no' || locale === 'nb' ? 'nb-NO' : locale;

/**
 * Price label for one tier, driven by the tier's currency.
 *
 * NOK keeps the Norwegian "1 900,-" convention the pages have always shown
 * (whole kroner, no currency code) — only the grouping follows the locale.
 * Every other currency uses `Intl`'s currency style, which puts the symbol
 * where the locale expects it (`€9.90` in English, `9,90 €` in Norwegian).
 */
export const formatTierPrice = (
    price: number,
    currency: CurrencyCode,
    locale: string,
): string => {
    const intlLocale = intlLocaleOf(locale);
    if (currency === 'NOK') {
        return `${new Intl.NumberFormat(intlLocale, { maximumFractionDigits: 0 }).format(price)},-`;
    }
    return new Intl.NumberFormat(intlLocale, { style: 'currency', currency }).format(price);
};

/** One tier, fully resolved for rendering. */
export interface TierView {
    /** Backend `subscriptionLevel` string. */
    id: string;
    name: string;
    /** Formatted price, e.g. `1 900,-` or `€9.90`. */
    priceLabel: string | null;
    /** Contact address shown instead of a price, else null. */
    contactEmail: string | null;
    /** Link text for the contact address (`subscription.tiers.<key>.contact`), else null. */
    contactLabel: string | null;
    features: string[];
    /** Usage-step description for metered tiers, else null. */
    metering: string | null;
    highlighted: boolean;
}

export const buildTierViews = (
    tiers: readonly AppTierConfig[],
    translate: TranslateFn,
    locale: string,
    currency: CurrencyCode,
): TierView[] => {
    const numberFormat = new Intl.NumberFormat(intlLocaleOf(locale));
    return tiers.map(tier => {
        const price = tier.prices ? priceIn(tier.prices, currency) : null;
        return {
        id: tier.id,
        name: translate(`subscription.tiers.${tier.i18nKey}.name`),
        priceLabel: price ? formatTierPrice(price.amount, price.currency, locale) : null,
        contactEmail: tier.contactEmail ?? null,
        contactLabel: tier.contactEmail
            ? translate(`subscription.tiers.${tier.i18nKey}.contact`)
            : null,
        features: Array.from({ length: tier.featureCount }, (_unused, index) =>
            translate(`subscription.tiers.${tier.i18nKey}.feature_${index}`),
        ),
        metering: tier.metered
            ? translate(
                  `subscription.tiers.${tier.i18nKey}.metering`,
                  Object.fromEntries(
                      Object.entries(tier.metered).map(([step, size]) => [
                          step,
                          numberFormat.format(size),
                      ]),
                  ),
              )
            : null,
        highlighted: tier.highlighted,
        };
    });
};

/**
 * Whether the flat-rate overage note applies. Metered tiers state their own
 * billing model on the card, so an app whose packaging is entirely metered
 * (ledger17) must not also claim "objects beyond the included amount…".
 */
export const showsOverageNote = (tiers: readonly AppTierConfig[]): boolean =>
    tiers.some(tier => !tier.metered && !tier.contactEmail);
